// uploadQueue.js - A new file to manage the upload queue
import { reactive } from 'vue';

class UploadQueueManager {
    constructor() {
        this.state = reactive({
            queue: [],
            activeUploads: new Map(),
            maxConcurrent: 4,
            totalFiles: 0,
            completedFiles: 0,
            failedFiles: 0,
            isProcessing: false
        });
    }

    // Add files to the queue
    addToQueue(files, uploadFunction, params) {
        const fileArray = Array.from(files);

        fileArray.forEach(file => {
            this.state.queue.push({
                file,
                uploadFunction,
                params: { ...params, fileData: file },
                status: 'queued',
                progress: 0,
                id: `${Date.now()}-${Math.random()}`,
                fileName: file.name
            });
        });

        this.state.totalFiles += fileArray.length;

        if (!this.state.isProcessing) {
            this.processQueue();
        }
    }

    // Process the queue
    async processQueue() {
        if (this.state.isProcessing) return;

        this.state.isProcessing = true;

        while (this.state.queue.length > 0 || this.state.activeUploads.size > 0) {
            // Start new uploads up to the concurrent limit
            while (this.state.activeUploads.size < this.state.maxConcurrent && this.state.queue.length > 0) {
                const item = this.state.queue.shift();
                this.startUpload(item);
            }

            // Wait for at least one upload to complete before continuing
            if (this.state.activeUploads.size > 0) {
                await this.waitForUploadSlot();
            }
        }

        this.state.isProcessing = false;
        this.state.totalFiles = 0;
        this.state.completedFiles = 0;
        this.state.failedFiles = 0;
    }

    // Start an individual upload
    async startUpload(item) {
        item.status = 'uploading';
        this.state.activeUploads.set(item.id, item);

        try {
            // Create a wrapper to intercept progress updates
            const originalParams = { ...item.params };
            originalParams.onProgress = (progress) => {
                item.progress = progress;
            };
            originalParams.fileName = item.fileName; // Pass filename for error reporting

            await item.uploadFunction(originalParams);
            item.status = 'completed';
            item.progress = 100;
            this.state.completedFiles++;
        } catch (error) {
            console.error('Upload failed:', error);
            item.status = 'failed';
            item.error = error;
            this.state.failedFiles++;

            // Don't stop processing other files - the queue will continue
        } finally {
            this.state.activeUploads.delete(item.id);
        }
    }

    // Wait for an upload slot to become available
    waitForUploadSlot() {
        return new Promise(resolve => {
            const checkSlot = setInterval(() => {
                if (this.state.activeUploads.size < this.state.maxConcurrent) {
                    clearInterval(checkSlot);
                    resolve();
                }
            }, 100);
        });
    }

    // Get current progress
    getProgress() {
        const total = this.state.totalFiles;
        if (total === 0) return 0;

        const completed = this.state.completedFiles + this.state.failedFiles;
        const activeProgress = Array.from(this.state.activeUploads.values())
            .reduce((sum, item) => sum + (item.progress || 0), 0) / 100;

        return Math.round(((completed + activeProgress) / total) * 100);
    }

    // Get active uploads
    getActiveUploads() {
        return Array.from(this.state.activeUploads.values());
    }

    // Get active upload count
    getActiveCount() {
        return this.state.activeUploads.size;
    }

    // Get queue length
    getQueueLength() {
        return this.state.queue.length;
    }

    // Get remaining files (queued + active)
    getRemainingFiles() {
        return this.state.queue.length + this.state.activeUploads.size;
    }

    // Clear the queue
    clearQueue() {
        this.state.queue = [];
        this.state.activeUploads.clear();
        this.state.totalFiles = 0;
        this.state.completedFiles = 0;
        this.state.failedFiles = 0;
        this.state.isProcessing = false;
    }
}

// Export a singleton instance
export const uploadQueue = new UploadQueueManager();